import { LifebuoyIcon } from "@heroicons/react/24/outline";
import { useAuthStore } from "@/store/authStore";

/**
 * The only thing on screen that says whose account this is.
 *
 * A WorkOS support session *is* the supported account - same person, same
 * offices, same everything - which is what makes it useful for reproducing what
 * somebody reported and exactly what makes this banner non-negotiable: without
 * it an operator has no way to tell they are typing into a stranger's gradebook.
 *
 * Deliberately not `ImpersonationBanner`. That one says "you are an admin
 * looking through a student's eyes" and offers the way back, and neither half is
 * true here: the operator holds no session of their own to return to, and the
 * only end to this one is its expiry. So there is no Exit control - an
 * inert-looking button that could not end the access would be worse than none.
 *
 * Both can be on screen at once. An operator supporting an admin gets the
 * admin's app, "View as" included, and each banner then answers its own
 * question: this one says whose account, that one says whose eyes.
 */
export function SupportSessionBanner() {
  const support = useAuthStore((state) => state.support);
  const user = useAuthStore((state) => state.user);

  if (!support) {
    return null;
  }

  const account = user ? `${user.firstName} ${user.lastName}` : "this account";

  return (
    <div
      role="status"
      // Sticky, unlike the impersonation banner below it, because the two are
      // hazards of different sizes. That one sits above a page an admin knows is
      // not theirs; this one sits above a page that looks exactly as it does to
      // its owner, and the edit an operator makes is usually further down than
      // the first screenful. A warning that scrolls away before the moment it
      // warns about is not one.
      className="sticky top-0 z-10 flex flex-wrap items-center gap-x-2 gap-y-1 bg-danger-700 px-4 py-2 text-sm text-white"
    >
      <LifebuoyIcon className="size-4 shrink-0" aria-hidden="true" />
      <span className="font-medium">Support session</span>
      <span>
        You are acting as{" "}
        <span className="rounded bg-white/20 px-2 py-0.5">{account}</span>
      </span>
      <span className="text-white/80">Started by {support.actorEmail}</span>
      {/* The only way this session ends, so it is worth saying out loud rather
          than leaving the operator to discover it as a sudden sign-out. */}
      <span className="text-white/80">
        Ends {new Date(support.endsAt * 1000).toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </span>
      {/* Omitted rather than printed empty: "Reason:" with nothing after it
          reads as a reason that was given and then lost. WorkOS only collects
          one where the environment is configured to require it. */}
      {support.reason && <span className="text-white/80">Reason: {support.reason}</span>}
    </div>
  );
}
